import { type FC } from "react";

import { Card } from "@/components/ui/Card";

interface HRVCardProperties {
  averageAwakeBpm?: number;
  averageHRV?: number;
  averageNightHeartRate?: number;
  averageRestBpm?: number;
  className?: string;
  maxHRV?: number;
  restingHeartRate?: number;
}

/**
 * HRV Bewertung basierend auf Altersgruppen (ms)
 *
 * @param value - HRV value in milliseconds
 *
 * @returns Status object with bgColor, color, and status
 *
 */
function getHRVStatus(value?: number): {
  bgColor: string;
  color: string;
  status: string;
} {
  if (!value) {
    return {
      bgColor: "bg-neutral-700",
      color: "text-neutral-400",
      status: "unknown",
    };
  }

  // Grobe HRV-Bewertung (kann je nach Alter variieren)
  if (value >= 50) {
    return {
      bgColor: "bg-green-900/20",
      color: "text-green-400",
      status: "excellent",
    };
  }
  if (value >= 35) {
    return {
      bgColor: "bg-blue-900/20",
      color: "text-blue-400",
      status: "good",
    };
  }
  if (value >= 25) {
    return {
      bgColor: "bg-yellow-900/20",
      color: "text-yellow-400",
      status: "fair",
    };
  }
  return { bgColor: "bg-red-900/20", color: "text-red-400", status: "poor" };
}

/**
 * Ruhepuls Bewertung
 *
 * @param value - Heart rate value in bpm
 *
 * @returns Status object with color and status
 *
 */
function getHeartRateStatus(value?: number): {
  color: string;
  status: string;
} {
  if (!value) {
    return { color: "text-neutral-400", status: "unknown" };
  }

  if (value <= 60) {
    return { color: "text-green-400", status: "excellent" };
  }
  if (value <= 70) {
    return { color: "text-blue-400", status: "good" };
  }
  if (value <= 80) {
    return { color: "text-yellow-400", status: "fair" };
  }
  return { color: "text-red-400", status: "high" };
}

/**
 * HRVCard - Zeigt Heart Rate Variability und Ruhepuls
 *
 * Features:
 * - Verwendet globale Card-Komponente für konsistentes Styling
 * - Durchschnittliche HRV
 * - Ruhepuls
 * - Gesundheitsbewertung basierend auf HRV
 * - Altersbasierte HRV-Bewertung
 *
 * @param root0
 *
 * @param root0.averageHRV
 *
 * @param root0.maxHRV
 *
 * @param root0.restingHeartRate
 *
 * @param root0.averageRestBpm
 *
 * @param root0.averageAwakeBpm
 *
 * @param root0.averageNightHeartRate
 *
 * @param root0.className
 *
 * @returns {JSX.Element} HRV card component
 *
 */
export const HRVCard: FC<HRVCardProperties> = ({
  averageAwakeBpm,
  averageHRV,
  averageNightHeartRate,
  averageRestBpm,
  className = "",
  maxHRV,
  restingHeartRate,
}) => {
  const hrvStatus = getHRVStatus(averageHRV);
  const heartRateStatus = getHeartRateStatus(restingHeartRate);
  const displayHRV = averageHRV ?? 0;

  // HRV Progress (0-100, basierend auf realistischen Werten)
  const hrvProgress = Math.min((displayHRV / 60) * 100, 100);

  return (
    <Card className={className} padding="sm" variant="default">
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <h3 className="text-sm font-medium text-neutral-200">HRV & Pulse</h3>
          <p className="text-xs text-neutral-400">HRV nur nachts verfügbar</p>
        </div>
        <div
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${hrvStatus.bgColor} ${hrvStatus.color}`}
        >
          {hrvStatus.status === "unknown" ? "N/A" : hrvStatus.status}
        </div>
      </div>

      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center">
          <div className="text-lg font-bold text-neutral-100">
            {averageHRV ? averageHRV.toFixed(1) : "N/A"}
          </div>
          <div className="text-xs text-neutral-400 ml-1">ms</div>
        </div>
        {maxHRV !== undefined && (
          <div className="text-right">
            <div className="text-xs text-neutral-400">Peak</div>
            <div className="text-sm font-medium text-green-400">{maxHRV.toFixed(1)} ms</div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-1.5">
        {averageRestBpm !== undefined && (
          <div className="text-left">
            <div className="text-xs text-neutral-400">Rest</div>
            <div className="text-sm font-medium text-neutral-200">{averageRestBpm} bpm</div>
          </div>
        )}
        {averageAwakeBpm !== undefined && (
          <div className="text-right">
            <div className="text-xs text-neutral-400">Awake</div>
            <div className="text-sm font-medium text-neutral-200">{averageAwakeBpm} bpm</div>
          </div>
        )}
      </div>

      {averageNightHeartRate !== undefined && (
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-left">
            <div className="text-xs text-neutral-400">Night</div>
            <div className={`text-sm font-medium ${heartRateStatus.color}`}>
              {averageNightHeartRate.toFixed(1)} bpm
            </div>
          </div>
        </div>
      )}

      {/* HRV Progress Bar */}
      <div className="w-full bg-neutral-700 rounded-full h-1">
        <div
          className={`h-1 rounded-full transition-all duration-300 ${hrvStatus.color.replace("text-", "bg-")}`}
          style={{ width: `${String(hrvProgress)}%` }}
        />
      </div>
    </Card>
  );
};
